import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { ExifData, InspectionImage } from "../_objects/inspection-image";
import { ImageMetadata } from "../_objects/image-metadata";
import * as ExifReader from "exifreader";
import { Tags } from "exifreader";
import { VISUAL_IQ_MODEL_NAME } from "../app.constants";
import { InspectionFileValidator } from "./image-upload/inspection-file.validator";

@Injectable({
  providedIn: "root"
})
export class ExifReaderService {
  constructor(private http: HttpClient) {}

  async getImageMetadata(inspectionImage: InspectionImage): Promise<ImageMetadata> {
    try {
      const blob = await this.fetchImageBlob(inspectionImage.filePath);
      let rawMetadata: ExifData;

      if (inspectionImage.mimeType === "image/bmp") {
        return null;
      } else {
        rawMetadata = await this.extractExifData(blob);
      }
      return new ImageMetadata(rawMetadata);
    } catch (err) {
      console.error("Failed to load image metadata:", err);
      return null;
    }
  }

  async isMentorVisualIqModelImage(file: File): Promise<boolean> {
    try {
      if (!InspectionFileValidator.isValidMediaFileType(file.type, file.name)) {
        return false;
      }

      if (file.type.startsWith("video/")) {
        return false;
      }

      const blob = new Blob([file]);

      const exifData = await this.extractExifData(blob);
      const imageMetadata = new ImageMetadata(exifData);
      const model = this.getModelFromExifData(imageMetadata);

      return model?.toLowerCase().includes("mentor visual iq") || false;
    } catch (err) {
      console.error("Failed to check model from EXIF data:", err);
      return false;
    }
  }

  /** Reads the EXIF and XMP tags of a file, or of a file the app can fetch such as a local `file:///` URL on desktop. */
  async getTags(source: Blob | string): Promise<Tags> {
    const blob = typeof source === "string" ? await this.fetchImageBlob(source) : source;
    return (await this.extractExifData(blob)).tags;
  }

  private getModelFromExifData(imageMetadata: ImageMetadata): string | null {
    const modelProperty = imageMetadata.metadata.get("Model");

    if (modelProperty) {
      return modelProperty.description; // Returns the description of the model
    }

    return null;
  }

  /** Fetches a file the app can read, such as a local `file:///` URL on desktop, to read its tags with `getTags` and scan its bytes. */
  async fetchImageBlob(filePath: string): Promise<Blob> {
    return firstValueFrom(this.http.get(filePath, { responseType: "blob" }));
  }

  private async extractExifData(blob: Blob): Promise<ExifData> {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const tags = ExifReader.load(arrayBuffer) as Tags;
      const fileType = tags?.FileType?.value || "unknown";
      const mimeType = this.getMimeType(fileType);

      return {
        tags,
        fileType: fileType,
        mimeType: mimeType
      };
    } catch (error) {
      console.error("Failed to extract EXIF data:", error);
      throw error;
    }
  }

  private getMimeType(fileType: string): string {
    switch (fileType.toLowerCase()) {
      case "jpeg":
      case "jpg":
        return "image/jpeg";
      case "png":
        return "image/png";
      case "gif":
        return "image/gif";
      case "bmp":
        return "image/bmp";
      case "tiff":
        return "image/tiff";
      case "webp":
        return "image/webp";
      case "heif":
        return "image/heif";
      case "ico":
        return "image/x-icon";
      default:
        return "unknown";
    }
  }
}
